import { Button, Col, Form, Input, Row, Spin, Switch } from 'antd';
import { useNavigate } from 'react-router';
import { incidencePaths } from 'constants/routePaths';
import { useEffect, useState } from 'react';
import { onInputHandlerToUpperCase } from 'libs/toUpperCase';
import { IncidenceCategory, IncidenceCategoryPayload } from 'types/incidences';
import { updateCategory } from 'api/incidence.api';
import * as Sentry from '@sentry/react';
import { HorizontalColorPicker } from 'components/common/Color/HorizontalColorPicker';
import { IncidenceCategoryPreview } from './IncidenceCategoryPreview';
import notificationHook from 'hooks/notificationHook';
import statusMessages from 'statusMessages';
import { ApiError } from 'types';
import { BackButton } from 'components/common/BackButton/BackButton';

type IncidenceCategoryEditProps = {
  category: IncidenceCategory;
};

type IncidenceCategoryFormValues = Pick<
  IncidenceCategoryPayload,
  'abbr' | 'desc' | 'color' | 'txtColor' | 'solicitable'
>;

// Intentional persisted category data, independent of the application theme.
const DEFAULT_CATEGORY_COLOR = '#FFFFFF';

const IncidenceCategoryEditForm = ({
  category,
}: IncidenceCategoryEditProps) => {
  const navigate = useNavigate();
  const requiredRule = { required: true, message: 'Este campo es obligatorio' };
  const [loading, setLoading] = useState(false);
  const [abbreviation, setAbbreviation] = useState(category.abbr);
  const [backgroundColor, setBackgroundColor] = useState(category.color);
  const [textColor, setTextColor] = useState(category.txtColor);
  const [form] = Form.useForm<IncidenceCategoryFormValues>();

  useEffect(() => {
    setAbbreviation(category.abbr);
    setBackgroundColor(category.color);
    setTextColor(category.txtColor);
    form.setFieldsValue({
      abbr: category.abbr,
      desc: category.desc,
      color: category.color,
      txtColor: category.txtColor,
      solicitable: category.solicitable ?? false,
    });
  }, [category, form]);

  const onFinish = (values: IncidenceCategoryFormValues) => {
    const payload: IncidenceCategoryPayload = {
      abbr: values.abbr,
      desc: values.desc,
      color: backgroundColor,
      txtColor: textColor,
      solicitable: category.protected
        ? (category.solicitable ?? false)
        : (values.solicitable ?? false),
    };

    setLoading(true);
    updateCategory(payload, category._id)
      .then(() => {
        notificationHook({
          type: 'success',
          message: 'Categoría de incidencia editada con éxito',
        });
        setLoading(false);
        navigate(`${incidencePaths.basePath}/${incidencePaths.catalogue}`);
      })
      .catch((error: unknown) => {
        Sentry.captureException(error);
        const currentStatus = (error as ApiError).statusCode;
        notificationHook({
          message: statusMessages.incidence[
            currentStatus ? currentStatus : 500
          ] as string,
          type: 'error',
        });
        setLoading(false);
      });
  };

  const onChangeColor = (hex: string, field: 'bg' | 'txt') => {
    if (field === 'bg') {
      setBackgroundColor(hex);
      form.setFieldValue('color', hex);
    } else {
      setTextColor(hex);
      form.setFieldValue('txtColor', hex);
    }
  };

  const onAbbreviationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAbbreviation(e.target.value);
  };

  return (
    <>
      <h2 className="title">Editar categoría de incidencia</h2>
      <BackButton />
      <Row justify="center" className="mt-5">
        <Col xs={10}>
          <Spin spinning={loading} tip="Editando categoría...">
            {category.protected && (
              <div
                className="incidence-category-protected-banner"
                data-cy="incidence-category-protected-note"
              >
                Esta incidencia está predefinida por el sistema y únicamente se
                puede editar el color de fondo y de texto.
              </div>
            )}
            <Form<IncidenceCategoryFormValues>
              form={form}
              onFinish={onFinish}
              autoComplete="off"
              labelCol={{ span: 6 }}
              data-cy="incidence-category-form"
            >
              <Form.Item name="abbr" label="Incidencia" rules={[requiredRule]}>
                <Input
                  type="text"
                  disabled={category.protected}
                  onInput={onInputHandlerToUpperCase}
                  onChange={onAbbreviationChange}
                  data-cy="incidence-category-abbr-input"
                />
              </Form.Item>
              <Form.Item name="desc" label="Descripción" rules={[requiredRule]}>
                <Input
                  type="text"
                  disabled={category.protected}
                  onInput={onInputHandlerToUpperCase}
                  data-cy="incidence-category-desc-input"
                />
              </Form.Item>

              <Row align="middle" justify="start">
                <Col xs={18}>
                  <Form.Item
                    name="color"
                    label="Color de fondo"
                    labelCol={{ flex: '0 0 140px' }}
                    wrapperCol={{ flex: '1 1 0' }}
                    rules={[requiredRule]}
                    className="mb-0 py-2"
                  >
                    <HorizontalColorPicker
                      onChange={(_, hex: string) => {
                        onChangeColor(hex, 'bg');
                      }}
                      defaultValue={backgroundColor ?? DEFAULT_CATEGORY_COLOR}
                    />
                  </Form.Item>
                  <Form.Item
                    name="txtColor"
                    label="Color de texto"
                    labelCol={{ flex: '0 0 140px' }}
                    wrapperCol={{ flex: '1 1 0' }}
                    rules={[requiredRule]}
                    className="mb-0 py-2"
                  >
                    <HorizontalColorPicker
                      onChange={(_, hex: string) => {
                        onChangeColor(hex, 'txt');
                      }}
                      defaultValue={textColor ?? DEFAULT_CATEGORY_COLOR}
                    />
                  </Form.Item>
                  <Form.Item
                    name="solicitable"
                    label="Solicitable"
                    valuePropName="checked"
                    labelCol={{ flex: '0 0 140px' }}
                    wrapperCol={{ flex: '1 1 0' }}
                    className="mb-0 py-2"
                  >
                    <Switch
                      disabled={category.protected}
                      data-cy="incidence-category-solicitable-switch"
                    />
                  </Form.Item>
                </Col>
                <Col>
                  <IncidenceCategoryPreview
                    text={abbreviation}
                    bgColor={backgroundColor}
                    txtColor={textColor}
                  />
                </Col>
              </Row>

              <Row justify="end" gutter={10} className="mt-10">
                <Col>
                  <Form.Item>
                    <Button
                      onClick={() =>
                        navigate(
                          `${incidencePaths.basePath}/${incidencePaths.catalogue}`
                        )
                      }
                      id="cancelBtn"
                    >
                      Regresar
                    </Button>
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item>
                    <Button htmlType="submit" type="primary">
                      Guardar
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Spin>
        </Col>
      </Row>
    </>
  );
};

export default IncidenceCategoryEditForm;
